import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../core/translate.pipe';
import { DsButtonDirective, DsInputDirective } from '../ui';
import { LocalizationService } from '../core/localization.service';
import {
  PlatformApi,
  type Campaign,
  type CampaignDashboard,
  type CreditBundle,
  type CurrentUser,
  type IssuedInvitation,
  type Organization,
  type OrganizationInvoice,
  type PublishedAssessment,
} from '../core/platform-api';

@Component({
  selector: 'app-company-page',
  imports: [ReactiveFormsModule, TranslatePipe, DsButtonDirective, DsInputDirective],
  templateUrl: './company-page.html',
  styleUrl: './company-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyPage {
  private readonly api = inject(PlatformApi);
  private readonly i18n = inject(LocalizationService);

  protected readonly user = signal<CurrentUser | null>(null);
  protected readonly organizations = signal<Organization[]>([]);
  protected readonly selectedOrganization = signal<Organization | null>(null);
  protected readonly assessments = signal<PublishedAssessment[]>([]);
  protected readonly campaigns = signal<Campaign[]>([]);
  protected readonly availableCredits = signal(0);
  protected readonly issuedInvitation = signal<IssuedInvitation | null>(null);
  protected readonly creditBundles = signal<CreditBundle[]>([]);
  protected readonly invoices = signal<OrganizationInvoice[]>([]);
  protected readonly dashboard = signal<CampaignDashboard | null>(null);
  protected readonly isBusy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly email = new FormControl('demo.owner@example.test', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });
  protected readonly organizationName = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(200)],
  });
  protected readonly campaignName = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(200)],
  });

  protected async login(): Promise<void> {
    if (this.email.invalid) {
      this.email.markAsTouched();
      return;
    }

    await this.run(async () => {
      this.user.set(await this.api.developmentLogin(this.email.value));
      await this.refreshOrganizations();
    });
  }

  protected async createOrganization(): Promise<void> {
    if (this.organizationName.invalid) {
      this.organizationName.markAsTouched();
      return;
    }

    await this.run(async () => {
      await this.api.createOrganization(this.organizationName.value);
      this.organizationName.reset();
      await this.refreshOrganizations();
    });
  }

  protected async selectOrganization(organization: Organization): Promise<void> {
    await this.run(async () => {
      this.selectedOrganization.set(organization);
      this.issuedInvitation.set(null);
      await this.refreshWorkspace();
    });
  }

  protected async grantDevelopmentCredits(): Promise<void> {
    const organization = this.selectedOrganization();
    if (!organization) return;
    await this.run(async () => {
      this.availableCredits.set(await this.api.grantDevelopmentCredits(organization.id, 10));
    });
  }

  protected async createCampaign(): Promise<void> {
    const organization = this.selectedOrganization();
    const assessment = this.assessments()[0];
    if (!organization || !assessment || this.campaignName.invalid) {
      this.campaignName.markAsTouched();
      return;
    }

    await this.run(async () => {
      const campaign = await this.api.createCampaign(
        organization.id,
        assessment.id,
        this.campaignName.value,
      );
      await this.api.activateCampaign(organization.id, campaign.id);
      this.campaignName.reset();
      await this.refreshWorkspace();
    });
  }

  protected async issueInvitation(campaign: Campaign): Promise<void> {
    const organization = this.selectedOrganization();
    if (!organization) return;
    await this.run(async () => {
      this.issuedInvitation.set(await this.api.issueInvitation(organization.id, campaign.id));
      this.availableCredits.set(await this.api.getCreditBalance(organization.id));
      this.dashboard.set(await this.api.getCampaignDashboard(organization.id));
    });
  }

  protected async purchaseBundle(bundle: CreditBundle): Promise<void> {
    const organization = this.selectedOrganization();
    if (!organization) return;
    await this.run(async () => {
      await this.api.purchaseDevelopmentBundle(organization.id, bundle.id);
      await this.refreshWorkspace();
    });
  }

  protected async refundInvoice(invoice: OrganizationInvoice): Promise<void> {
    const organization = this.selectedOrganization();
    if (!organization) return;
    await this.run(async () => {
      await this.api.refundOrganizationInvoice(organization.id, invoice.id);
      await this.refreshWorkspace();
    });
  }

  private async refreshOrganizations(): Promise<void> {
    this.organizations.set(await this.api.listOrganizations());
    if (!this.selectedOrganization() && this.organizations().length > 0) {
      this.selectedOrganization.set(this.organizations()[0]);
      await this.refreshWorkspace();
    }
  }

  private async refreshWorkspace(): Promise<void> {
    const organization = this.selectedOrganization();
    if (!organization) return;
    const [assessments, campaigns, credits, bundles, invoices, dashboard] = await Promise.all([
      this.api.listPublishedAssessments(),
      this.api.listCampaigns(organization.id),
      this.api.getCreditBalance(organization.id),
      this.api.listCreditBundles(organization.id),
      this.api.listOrganizationInvoices(organization.id),
      this.api.getCampaignDashboard(organization.id),
    ]);
    this.assessments.set(assessments);
    this.campaigns.set(campaigns);
    this.availableCredits.set(credits);
    this.creditBundles.set(bundles);
    this.invoices.set(invoices);
    this.dashboard.set(dashboard);
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.isBusy.set(true);
    this.error.set(null);
    try {
      await action();
    } catch {
      this.error.set(this.i18n.t('company.error'));
    } finally {
      this.isBusy.set(false);
    }
  }
}
